import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setMarketplaceCartQuantity } from '../../redux/reducers/marketplaceCart';
import SpecimenCard from '../../components/market-place/specimen-card/SpecimenCard';
import SpecimenListItem from '../../components/market-place/specimen-card/SpecimenListItem';
import SpecimenCardSkeleton from '../../components/market-place/specimen-card/SpecimenCardSkeleton';
import SpecimenListItemSkeleton from '../../components/market-place/specimen-card/SpecimenListItemSkeleton';
import SpecimenTableItem from '../../components/market-place/specimen-table/SpecimenTableItem';
import ToggleView from '../../components/toggle-view/ToggleView';
import { FilterIconSvg } from '../../components/svgs';
import { FilterDropdown } from '../../components';
import MarketPlaceFilters, { FilterSelection } from './MarketPlaceFilters';
import { MARKETPLACE_CONSTANTS } from '../../constants/common';
import { toastMessages } from '../../constants/errorMessages';
import { ROUTE_PATHS } from '../../constants/routePaths';
import { MarketPlaceSpecimenResponse } from '../../types/interfaces/marketPlace.interfaces';
import {
  StandardParamsToODataParams,
  StandardParams,
  ODataParams,
  PagedResponse,
} from '../../types/interfaces/apiParams.interfaces';
import { SortOptionEnum } from '../../types/enums/marketPlace.enum';
import { ViewMode } from '../../types/enums/viewMode.enum';
import { addToMarketplaceCart, getMarketplaceCartItems } from '../../api/marketPlaceCartItemApi';
import { getMarketplaceSpecimensOData } from '../../api/marketPlaceSpecimenApi';
import { showToast } from '../../services/toast.service';
import { isWithinStockLimit, getActiveFilterCount } from '../../utils/commonUtils';
import { useEqualizeSpecimenCardHeaders } from '../../hooks/useEqualizeSpecimenCardHeaders';
import { useGuestMarketplaceOptional } from '../../context/guestMarketplaceContext';
import { Genders } from '../../types/enums';
import './marketPlace.scss';

interface MarketplaceFilters {
  search: string;
  sort: string | undefined;
  selection: FilterSelection | undefined;
}

const pageSize = 8;
const MOBILE_BREAKPOINT_PX = 767;

const MARKETPLACE_CART_ITEMS_INCLUDE = 'Accounts, MarketPlaceSpecimen.Specimen.Animal, MarketPlaceSpecimen.Specimen.SpecimenType';
const MARKETPLACE_INCLUDES = 'Animal.Breed.Species, Animal.Attachments, Accounts, MarketPlaceSpecimenTagMappings';

type SortDropdownOption = { name: string; value: string | undefined };

const SORT_OPTIONS: SortDropdownOption[] = [
  { value: undefined, name: 'Sort by' },
  { value: 'PriceAsc', name: 'Price: Low to High' },
  { value: 'PriceDesc', name: 'Price: High to Low' },
  { value: 'Newest', name: 'Youngest Cattle' },
  { value: 'Oldest', name: 'Oldest Cattle' },
];

export const buildODataParams = (
  search?: string,
  sort?: string,
  filters?: FilterSelection,
  offset = 0,
  top = pageSize,
): ODataParams => {
  const standard: StandardParams = { include: MARKETPLACE_INCLUDES, filter: undefined, sort: undefined, offset, limit: top };

  if (search && search.trim().length > 0) {
    const searchQuery = search.trim().replace(/'/g, "''");
    standard.filter = `(contains(Animal/Name,'${searchQuery}') or contains(Animal/Breed/Name,'${searchQuery}') or contains(Animal/Breed/Species/Name,'${searchQuery}')) and IsDeleted eq false`;
  } else {
    standard.filter = `IsDeleted eq false and InStock eq true`;
  }

  if (filters) {
    const taxClauses: string[] = [];
    if (filters.speciesIds?.length) {
      taxClauses.push('(' + filters.speciesIds.map(id => `Animal/Breed/SpeciesId eq ${id}`).join(' or ') + ')');
    }
    if (filters.breedIds?.length) {
      taxClauses.push('(' + filters.breedIds.map(id => `Animal/BreedId eq ${id}`).join(' or ') + ')');
    }

    const otherClauses: string[] = [];
    if (filters.specimenTypeIds?.length) {
      otherClauses.push('(' + filters.specimenTypeIds.map(id => `Specimen/SpecimenTypeId eq ${id}`).join(' or ') + ')');
    }
    if (filters.minPriceCents !== undefined) otherClauses.push(`UnitPriceInCents ge ${filters.minPriceCents}`);
    if (filters.maxPriceCents !== undefined) otherClauses.push(`UnitPriceInCents le ${filters.maxPriceCents}`);

    const combinedParts: string[] = [];
    if (taxClauses.length) combinedParts.push('(' + taxClauses.join(' or ') + ')');
    if (otherClauses.length) combinedParts.push('(' + otherClauses.join(' and ') + ')');

    if (combinedParts.length) {
      standard.filter = standard.filter ? `${standard.filter} and (${combinedParts.join(' and ')})` : combinedParts.join(' and ');
    }
  }

  if (sort === SortOptionEnum.PRICE_ASC) {
    standard.sort = 'UnitPriceInCents';
  } else if (sort === SortOptionEnum.PRICE_DESC) {
    standard.sort = '^UnitPriceInCents';
  } else if (sort === SortOptionEnum.NEWEST) {
    standard.sort = '^Animal/BirthDate';
  } else if (sort === SortOptionEnum.OLDEST) {
    standard.sort = 'Animal/BirthDate';
  }

  return StandardParamsToODataParams(standard);
};

function parseMarketplaceODataBody(data: MarketPlaceSpecimenResponse[] | PagedResponse<MarketPlaceSpecimenResponse>): {
  result: MarketPlaceSpecimenResponse[];
  total: number;
} {
  if (Array.isArray(data)) {
    return { result: data, total: data.length };
  }
  return { result: data.result ?? [], total: data.total ?? 0 };
}

const quantityForMarketplaceCart = (specimen: MarketPlaceSpecimenResponse): number => {
  const isFemale = isFemaleSpecimen(specimen);
  return isFemale ? Math.max(specimen.quantity ?? 1, 1) : 1;
};

const isFemaleSpecimen = (specimen: MarketPlaceSpecimenResponse): boolean => {
  const gender = specimen.animal?.gender ?? specimen.specimen?.animal?.gender;
  return String(gender ?? '').toLowerCase() === Genders.Female.toLowerCase();
};

export type MarketplaceProps = {
  guestMode?: boolean;
};

const Marketplace: FC<MarketplaceProps> = ({ guestMode = false }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const guestMarketplace = useGuestMarketplaceOptional();
  const [isMobileView, setIsMobileView] = useState<boolean>(false);
  const activeAccountId = useAppSelector(state => state.activeAccount.accountId);
  const [specimens, setSpecimens] = useState<MarketPlaceSpecimenResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
  const [pagingOffset, setPagingOffset] = useState<number>(0);
  const [pagingMax, setPagingMax] = useState<number>(0);
  const [viewType, setViewType] = useState<ViewMode>(ViewMode.Card);
  const [filters, setFilters] = useState<MarketplaceFilters>({ search: '', sort: undefined, selection: undefined });

  const filterSelection = guestMode ? guestMarketplace?.activeFilters : filters.selection;

  const isLoadingRef = useRef(false);
  const marketplaceQueryGenerationRef = useRef(0);
  const itemsContainerRef = useRef<HTMLDivElement>(null);

  useEqualizeSpecimenCardHeaders({
    containerRef: itemsContainerRef,
    enabled: viewType === ViewMode.Card,
    itemCount: specimens.length,
    isLoading,
  });

  const fetchSpecimens = useCallback(
    async (offset: number, currentSpecimens: MarketPlaceSpecimenResponse[]) => {
      if (isLoadingRef.current && offset > 0) return;

      if (offset === 0) {
        marketplaceQueryGenerationRef.current += 1;
      }
      const generation = marketplaceQueryGenerationRef.current;

      isLoadingRef.current = true;
      setIsLoading(true);

      try {
        const odata = buildODataParams(filters.search, filters.sort, filterSelection, offset, pageSize);
        const res = await getMarketplaceSpecimensOData(odata, true);

        if (generation !== marketplaceQueryGenerationRef.current) {
          return;
        }

        const { result: newSpecimens, total } = parseMarketplaceODataBody(res.data);

        setSpecimens([...currentSpecimens, ...newSpecimens]);
        setPagingMax(total);
        setPagingOffset(Math.min(offset + pageSize, total));
      } catch {
        if (generation === marketplaceQueryGenerationRef.current) {
          showToast.error(toastMessages.FAILED_TO_FETCH_MARKETPLACE_SPECIMENS);
        }
      } finally {
        if (generation === marketplaceQueryGenerationRef.current) {
          setIsLoading(false);
          isLoadingRef.current = false;
        }
      }
    },
    [filters.search, filters.sort, filterSelection],
  );

  useEffect(() => {
    setSpecimens([]);
    setPagingOffset(0);
    void fetchSpecimens(0, []);
  }, [fetchSpecimens]);

  useEffect(() => {
    if (viewType === ViewMode.Table) return;

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      if (scrollHeight - scrollTop - clientHeight < 600 && !isLoadingRef.current && !isLoading && pagingOffset < pagingMax) {
        void fetchSpecimens(pagingOffset, specimens);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isLoading, pagingOffset, pagingMax, fetchSpecimens, specimens, viewType]);

  useEffect(() => {
    if (guestMode && guestMarketplace) {
      guestMarketplace.setListingProductCount(specimens.length);
    }
  }, [guestMode, guestMarketplace, specimens]);

  useEffect(() => {
    const handleResize = (): void => {
      setIsMobileView(window.innerWidth <= MOBILE_BREAKPOINT_PX);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (guestMode && !isMobileView) {
      setFiltersOpen(false);
    }
  }, [guestMode, isMobileView]);

  const refreshMarketplaceCartQuantity = useCallback(async () => {
    try {
      const { data: cartItems } = await getMarketplaceCartItems({
        include: MARKETPLACE_CART_ITEMS_INCLUDE,
        filter: `accountId eq ${activeAccountId}`,
      });
      const totalQty = (cartItems ?? []).reduce((sum, item) => sum + (item.cartQuantity ?? 0), 0);
      dispatch(setMarketplaceCartQuantity(totalQty));
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  }, [activeAccountId, dispatch]);

  const handleAddToCart = useCallback(
    async (specimen: MarketPlaceSpecimenResponse) => {
      const quantity = quantityForMarketplaceCart(specimen);
      const isFemale = isFemaleSpecimen(specimen);

      if (guestMode) {
        const availableStock = specimen.quantity ?? 0;
        if (!isWithinStockLimit(specimen.marketPlaceSpecimenId, quantity, availableStock)) {
          showToast.error(toastMessages.MARKETPLACE_CART_ITEM_MAXED_OUT);
          return;
        }
        guestMarketplace?.addGuestSpecimenToCart(specimen, quantity);
        showToast.success(toastMessages.ADD_TO_MARKETPLACE_CART_SUCCESS);
        return;
      }

      if (activeAccountId == null) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        return;
      }

      try {
        await addToMarketplaceCart({
          accountId: activeAccountId,
          quantity,
          sellerAccountId: specimen.accountId,
          specimenId: specimen.specimenId,
          marketPlaceSpecimenId: specimen.marketPlaceSpecimenId,
          ...(isFemale ? { embryoStrawId: specimen.embryoStrawId ?? 0 } : {}),
        });
        showToast.success(toastMessages.ADD_TO_MARKETPLACE_CART_SUCCESS);
        await refreshMarketplaceCartQuantity();
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    },
    [activeAccountId, guestMarketplace, guestMode, refreshMarketplaceCartQuantity],
  );

  const filterCount = useMemo(() => {
    if (guestMode && !isMobileView) return 0;
    return getActiveFilterCount(guestMode ? filterSelection : filters.selection);
  }, [guestMode, isMobileView, filterSelection, filters.selection]);

  const showFilterButton = !guestMode || isMobileView;

  const previewPath = useCallback(
    (id: number) => (guestMode ? `${ROUTE_PATHS.PUBLIC_MARKETPLACE_}${id}` : ROUTE_PATHS.APP_MARKETPLACE_VIEW + id),
    [guestMode],
  );

  const isAddToCartDisabled = (specimen: MarketPlaceSpecimenResponse) =>
    !guestMode && activeAccountId != null && activeAccountId === specimen.accountId;

  const renderSkeletons = (count: number) =>
    Array.from({ length: count }).map((_, index) =>
      viewType === ViewMode.Card ? (
        <SpecimenCardSkeleton key={`card-skeleton-${index}`} />
      ) : (
        <SpecimenListItemSkeleton key={`list-skeleton-${index}`} />
      ),
    );

  const rootClassName = guestMode ? 'marketplace marketplace--guest-listing' : 'marketplace';
  const controlsClassName = guestMode ? 'marketplace__controls marketplace__controls--guest' : 'marketplace__controls';

  return (
    <>
      <div className={rootClassName}>
        <div className={controlsClassName}>
          {showFilterButton && (
            <div className="marketplace__filter-button">
              <button className="marketplace__filter-trigger" type="button" onClick={() => setFiltersOpen(true)}>
                <FilterIconSvg />
                <span className="marketplace__filter-text">{MARKETPLACE_CONSTANTS.FILTER}</span>
              </button>
              {filterCount > 0 && <span className="marketplace__filter-count">{filterCount}</span>}
            </div>
          )}
          <div className="marketplace__search">
            <input
              type="search"
              placeholder="Search by animal name, Species or breed"
              value={filters.search}
              onChange={event => setFilters(prev => ({ ...prev, search: event.target.value }))}
            />
          </div>
          <div className="marketplace__sort">
            <FilterDropdown
              options={SORT_OPTIONS}
              value={SORT_OPTIONS.find(option => option.value === filters.sort) ?? SORT_OPTIONS[0]}
              onChange={(option: SortDropdownOption) => setFilters(prev => ({ ...prev, sort: option.value || undefined }))}
              showSearch={false}
              placeholder="Sort by"
            />
          </div>
          <div className="marketplace__view-toggle">
            <ToggleView value={viewType} onChange={setViewType} />
          </div>
        </div>
        {!guestMode && (
          <MarketPlaceFilters
            isOpen={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            initial={filters.selection}
            onApply={(selection: FilterSelection) => setFilters(prev => ({ ...prev, selection }))}
            productCount={specimens.length}
          />
        )}
        {guestMode && isMobileView && (
          <MarketPlaceFilters
            isOpen={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            initial={filterSelection}
            onApply={(selection: FilterSelection) => guestMarketplace?.setActiveFilters(selection)}
            productCount={guestMarketplace?.listingProductCount}
          />
        )}

        {viewType === ViewMode.Table ? (
          <SpecimenTableItem
            search={filters.search}
            sort={filters.sort}
            selection={filterSelection}
            isAddToCartDisabled={isAddToCartDisabled}
            onPreview={specimen => navigate(previewPath(specimen.marketPlaceSpecimenId))}
            onAddToCart={specimen => void handleAddToCart(specimen)}
          />
        ) : !isLoading && specimens.length === 0 ? (
          <div className="marketplace__empty">{MARKETPLACE_CONSTANTS.NO_MARKETPLACE_SPECIMEN}</div>
        ) : (
          <div
            ref={itemsContainerRef}
            className={`marketplace__items ${
              viewType === ViewMode.List ? 'marketplace__items--list' : 'marketplace__items--card'
            }`}>
            {specimens.length === 0
              ? renderSkeletons(pageSize)
              : specimens.map((specimen: MarketPlaceSpecimenResponse) => {
                  const SpecimenComponent = viewType === ViewMode.Card ? SpecimenCard : SpecimenListItem;
                  return (
                    <SpecimenComponent
                      key={specimen.marketPlaceSpecimenId}
                      animalId={specimen.animal?.animalId}
                      name={specimen.animal?.name}
                      attachments={specimen.animal?.attachments}
                      breed={specimen.animal?.breed?.name}
                      species={specimen.animal?.breed?.species?.name}
                      gender={specimen.animal?.gender}
                      quantity={specimen.quantity}
                      price={specimen.unitPriceInCents}
                      accountId={specimen.accountId}
                      breederName={specimen.account?.name}
                      marketplaceSpecimenTagIds={specimen.marketPlaceSpecimenTagMappings?.map(
                        marketPlaceSpecimenTagMapping => marketPlaceSpecimenTagMapping.marketPlaceSpecimenTagId,
                      )}
                      disableAddToCart={isAddToCartDisabled(specimen)}
                      onPreview={() => navigate(previewPath(specimen.marketPlaceSpecimenId))}
                      onAddToCart={() => void handleAddToCart(specimen)}
                    />
                  );
                })}
            {isLoading && specimens.length > 0 && renderSkeletons(pageSize)}
          </div>
        )}
      </div>
    </>
  );
};

export default Marketplace;
